import { Capacitor } from '@capacitor/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import { BottomSheet } from '@/@bothrs-ui/web/components/bottom-sheet/BottomSheet'
import { FlexRow, Padding } from '@/@bothrs-ui/web/components/layout'
import { Markdown } from '@/@bothrs-ui/web/components/markdown/Markdown'
import { Modal } from '@/@bothrs-ui/web/components/modal/Modal'
import { H2 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'

type InfoSheetProps = {
  title?: string
  content: string // markdown
  onOpen?: () => void
}

const InfoSheet = ({ title, content, onOpen }: InfoSheetProps) => {
  const theme = useTheme()
  const [isOpen, setIsOpen] = useState(false)
  const isMobile = useMediaQuery(breakpoints.toS) || Capacitor.isNativePlatform()

  const openHandler = () => {
    setIsOpen(true)
    onOpen?.()
  }

  const closeHandler = () => {
    setIsOpen(false)
  }

  const markdown = <Markdown content={content} typography='BodyMediumRegular' />

  return (
    <>
      <StInfoButton type='button' onClick={openHandler} aria-label='Extra informatie'>
        <FontAwesomeIcon icon={['fal', 'info-circle']} fontSize={20} />
      </StInfoButton>

      {isMobile ? (
        <BottomSheet fitContent visible={isOpen} onClose={closeHandler}>
          <Padding
            px={theme.UI.Spacing.Space4}
            pb={theme.UI.Spacing.Space6}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            {title ? <H2>{title}</H2> : null}
            {markdown}
          </Padding>
        </BottomSheet>
      ) : (
        <Modal isVisible={isOpen} onClose={closeHandler} maxHeight='90vh'>
          <StModalContent>
            <FlexRow justifyContent='space-between' alignItems='center'>
              {title ? <H2 noMargin>{title}</H2> : <span aria-hidden='true' />}
              <StCloseButton type='button' onClick={closeHandler} aria-label='Sluiten'>
                <FontAwesomeIcon icon={['far', 'close']} fontSize={20} color={theme.Colors.Neutrals.Darker} />
              </StCloseButton>
            </FlexRow>
            {markdown}
          </StModalContent>
        </Modal>
      )}
    </>
  )
}

export { InfoSheet }

const StInfoButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  margin-left: ${({ theme }) => theme.UI.SpacingPx.Space2};
  color: ${({ theme }) => theme.Colors.Theme.Primary};
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.Colors.Neutrals.Dark};
  }

  &:focus-visible {
    color: ${({ theme }) => theme.Colors.Neutrals.Dark};
    outline: 2px solid ${({ theme }) => theme.Colors.Theme.Primary};
    outline-offset: 2px;
    border-radius: 2px;
  }
`

const StCloseButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
`

const StModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
`
